import { PencilSimple, X } from "@phosphor-icons/react"
import { AnimatePresence, motion } from "motion/react"
import { shuffle } from "lodash"
import * as React from "react"
import { type JSX, useEffect, useLayoutEffect, useRef, useImperativeHandle } from "react"
import { useDebounceValue } from "usehooks-ts"
import { cva, type VariantProps } from "class-variance-authority"

import { Label } from "~/components/reusable/Label"
import { Tooltip } from "~/components/reusable/Tooltip"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { beginEdit } from "~/utils/editSession"
import { formatNumber } from "~/utils/formatting"
import { cn } from "~/utils/helpers"

import { usePermissionGate, type PermissionGateProps } from "./PermissionTooltip"

const inputVariants = cva(
  [
    "flex w-full rounded-full px-3 paragraph-base !text-fg-base transition",
    "placeholder:text-fg-muted",
    "focus-visible:outline-hidden [&:focus,&.forceFocus]:outline-hidden",
    "disabled:cursor-not-allowed disabled:text-fg-disabled disabled:placeholder:text-fg-disabled",
    "read-only:cursor-default read-only:text-fg-muted",
  ],
  {
    variants: {
      variant: {
        default: "",
        transparent: [
          "bg-transparent",
          "[&:hover,&.forceHover]:bg-button-transparent-hover",
          "[&:focus,&.forceFocus]:bg-button-transparent-hover",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-shadow",
          "read-only:bg-transparent read-only:[&:hover,&.forceHover]:bg-transparent",
        ],
        invisible: "bg-transparent",
        edited: [
          "bg-adjusted-bg border border-adjusted-border/70 !text-adjusted-fg pr-7",
          "[&:hover,&.forceHover]:bg-adjusted-bg",
          "[&:focus,&.forceFocus]:border-adjusted-focus-border",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-shadow",
        ],
      },
      size: {
        default: "h-8 py-1.5",
        small: "h-7 py-1",
      },
      color: {
        field: "",
        "field-component": "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        color: "field",
        class: [
          "bg-field shadow-borders-base",
          "[&:hover,&.forceHover]:bg-field-hover",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-active",
          "disabled:bg-disabled disabled:shadow-borders-base",
          "read-only:[&:hover,&.forceHover]:bg-field",
        ],
      },
      {
        variant: "default",
        color: "field-component",
        class: [
          "bg-field-component shadow-borders-base",
          "[&:hover,&.forceHover]:bg-field-component-hover",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-active",
          "disabled:bg-disabled disabled:shadow-borders-base",
          "read-only:[&:hover,&.forceHover]:bg-field-component",
        ],
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      color: "field",
    },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "color">,
    Omit<VariantProps<typeof inputVariants>, "size">,
    PermissionGateProps {
  containerClassName?: string
  clearButton?: boolean
  label?: string
  small?: boolean // We can't use size as that is a prop on the input element
  icon?: JSX.Element
  infoElement?: JSX.Element
  unit?: string
  editedTitle?: string
  /** Error text under the field. Also puts the field in its error state. */
  error?: string
}

/**
 * Single-line text input — forms, tables, toolbars. Variants pick by surface (default / transparent / invisible / edited).
 * Color contrasts with the surface: `field` (default, gray fill) on white (bg-base) or in a white wrapper;
 * `field-component` (white fill) only when placed directly on a gray surface (bg-background).
 * For multi-line text use Textarea; for dates use DatePicker / DateRangePicker.
 * Canonical intent: {@link ../design-system/intents.ts TEXT_INPUT_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName = "w-[140px]",
      type,
      small,
      clearButton = false,
      infoElement,
      unit,
      error,
      variant,
      color,
      editedTitle,
      title,
      disabled,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    // No default wrapper width: the field's width lives in the caller's `containerClassName`,
    // so the caller also passes `wrapperClassName` when it stretches the field.
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      // The gate's wrapper covers the label too, so a gated field's `title` belongs here
      // instead of on the label — one tooltip per control.
      allowedTitle: allowedTitle ?? title,
      tooltipSide,
      wrapperClassName,
    })
    // Gated, `title` is the gate's copy: keep it off both the label tooltip and the input's
    // native one, or the browser bubble opens alongside the gate's.
    const ownTitle = requires === undefined ? title : undefined
    const isEdited = variant === "edited"
    const errorId = React.useId()
    const invalid = Boolean(error) || Boolean(props["aria-invalid"])
    const field = (
      <div className={cn("relative", containerClassName)}>
        {props.label && (
          <Tooltip title={ownTitle} className="w-[280px]" side="top">
            <Label htmlFor={props.id}>{props.label}</Label>
          </Tooltip>
        )}
        {props.icon}
        <input
          type={type}
          className={cn(
            inputVariants({
              variant,
              size: small ? "small" : "default",
              color,
            }),
            clearButton && "pr-8",
            unit && "pr-12",
            invalid && "focus:shadow-borders-error shadow-borders-error",
            className,
          )}
          ref={ref}
          title={ownTitle}
          disabled={disabled || blocked}
          {...props}
          aria-invalid={invalid || undefined}
          aria-describedby={error ? errorId : props["aria-describedby"]}
        />
        {isEdited && (
          <span
            title={editedTitle ?? "Unsaved edit"}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-adjusted-fg flex items-center cursor-default pointer-events-none"
            aria-label={editedTitle ?? "Unsaved edit"}
          >
            <PencilSimple size={13} weight="regular" />
          </span>
        )}
        {clearButton && (props.onChange || props.onBlur) && (
          <X
            size="16"
            // Offset from the bottom, not `top-1/2`, so a label above the field can't push it
            // off-centre — sized to centre the 16px icon in the field's own height.
            className={cn("absolute right-2 cursor-pointer", small ? "bottom-1.5" : "bottom-2")}
            onClick={() => {
              if (props.onChange) props.onChange!({ target: { value: "" } } as any)
              if (props.onBlur) props.onBlur!({ target: { value: "" } } as any)
            }}
          />
        )}
        {unit && (
          <div className={cn("absolute right-0 top-0 flex items-center", small ? "h-7" : "h-8")}>
            <div className="h-full px-2 flex items-center bg-subtle rounded-r-full border-l border-base">
              <span className="text-sm text-fg-muted font-medium">{unit}</span>
            </div>
          </div>
        )}
        {!props.defaultValue && infoElement}
        {error && (
          <p id={errorId} className="paragraph-sm text-fg-negative-text mt-1">
            {error}
          </p>
        )}
      </div>
    )

    return gate(field)
  },
)
Input.displayName = "Input"

export interface DebounceInputProps extends InputProps {
  debounceTime?: number
  placeholders?: string[]
  shufflePlaceholders?: boolean
  small?: boolean // We can't use size as that is a prop on the input element
}

const DebouncedInput = React.forwardRef<HTMLInputElement, DebounceInputProps>(
  (
    {
      onChange,
      debounceTime = 500,
      placeholders = [],
      shufflePlaceholders = true,
      small,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = React.useState<string>("")
    const [debouncedValue] = useDebounceValue(inputValue, debounceTime)

    const [currentPlaceholder, setCurrentPlaceholder] = React.useState(0)
    const [randomizedPlaceholders, setRandomizedPlaceholders] = React.useState<string[]>([])
    const [hasChanges, setHasChanges] = React.useState(false)

    const shuffledPlaceholders = (placeholders: string[]) => {
      if (placeholders.length <= 1 || !shufflePlaceholders) return placeholders
      const [first, ...rest] = placeholders
      return [first!, ...shuffle(rest)]
    }

    useEffect(() => {
      if (placeholders.length > 0) {
        const randomizedPlaceholders = shuffledPlaceholders(placeholders)
        setRandomizedPlaceholders(randomizedPlaceholders ?? [])
        setCurrentPlaceholder(0)
        let currentIndex = 0

        const interval = setInterval(() => {
          currentIndex = (currentIndex + 1) % randomizedPlaceholders.length
          setCurrentPlaceholder(currentIndex)
        }, 3000)

        return () => clearInterval(interval)
      }
    }, [placeholders])

    useEffect(() => {
      onChange?.({ target: { value: debouncedValue } } as React.ChangeEvent<HTMLInputElement>)
    }, [debouncedValue])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      setHasChanges(true)
    }

    return (
      <div className={cn("relative", props.containerClassName)}>
        <Input
          ref={ref}
          onChange={handleInputChange}
          placeholder={placeholders.length > 0 ? undefined : props.placeholder}
          small={small}
          {...props}
        />
        {placeholders.length > 0 && inputValue === "" && !(!hasChanges && props.defaultValue) && (
          <AnimatePresence>
            <motion.div
              key={`placeholder-${currentPlaceholder}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5 }}
              className={cn(
                "absolute inset-0 flex items-center text-fg-disabled paragraph-base px-2 pointer-events-none",
                props.className,
              )}
            >
              {randomizedPlaceholders[currentPlaceholder]}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    )
  },
)
DebouncedInput.displayName = "DebounceInput"

export interface NumericInputProps extends Omit<InputProps, "value" | "onChange" | "min" | "max"> {
  value: number | string
  /** Fires on the Enter or blur that ends a typed edit, an emptied field as 0. Prefer `onCommit`. */
  onChange?: (value: number) => void
  /**
   * Fires once per edit the planner typed, on Enter or blur: the number, or null for an emptied
   * field. Never on a focus without typing, on Escape, or for a value outside `min`/`max`
   * (with `clampToRange`, the nearest bound instead).
   */
  onCommit?: (value: number | null) => void
  allowDecimals?: boolean
  allowNegative?: boolean
  allowZero?: boolean
  /**
   * A typed value outside these is refused with a message under the field, shown once typing pauses.
   * Enter or blur keeps the refused value and its message without committing; Escape restores the
   * last value. `min` defaults to 0 unless `allowNegative`.
   */
  min?: number
  max?: number
  /** A typed value outside `min`/`max` still shows the message, then commits the nearest bound. */
  clampToRange?: boolean
  /** Why the typed value can't be committed, on every keystroke and refused commit; null once it can. */
  onRefusalChange?: (refusal: string | null) => void
  /** Display precision. Defaults to 2 with `allowDecimals`, 0 without. */
  decimals?: number
  /** Always show `decimals` digits (62,0 rather than 62). */
  fixedDecimals?: boolean
  locale?: string
}

/**
 * de-DE reads "." as a thousands separator, but a "." the planner types into a decimal field is the
 * decimal point (12.5, not 125). `caret` sits just after that keystroke.
 */
export const typedDotAsDecimal = (value: string, caret: number): string =>
  value[caret - 1] === "." && !value.includes(",")
    ? `${value.slice(0, caret - 1)},${value.slice(caret)}`
    : value

/** Typed integer digits with thousands separators, exact past 2⁵³ where a number would round. */
export const groupTypedDigits = (digits: string, locale: string): string =>
  /^\d+$/.test(digits) ? BigInt(digits).toLocaleString(locale) : digits

// A bound is shown as set (99.5, not 100), whatever the field's display precision.
const formatBound = (bound: number) => formatNumber(bound, 10)

/** Why `num` can't be committed to a field bounded by `min`/`max`, or null when it can. */
export const numericRefusal = (num: number, min: number, max: number): string | null => {
  if (num >= min && num <= max) return null
  if (Number.isFinite(min) && Number.isFinite(max))
    return `Enter a value from ${formatBound(min)} to ${formatBound(max)}.`
  if (num < min)
    return min === 0
      ? "Negative numbers can't be saved here."
      : `Enter ${formatBound(min)} or more.`
  return `Enter ${formatBound(max)} or less.`
}

/** Why a typed raw value (digits, "-", ".") can't be committed, or null while it can or holds no digit. */
export const typedRefusal = (raw: string, min: number, max: number): string | null =>
  /\d/.test(raw) ? numericRefusal(parseFloat(raw), min, max) : null

const REFUSAL_DELAY_MS = 300

function computeNumericInputState(
  value: number | string,
  allowZero: boolean,
  allowDecimals: boolean,
  decimals?: number,
  fixedDecimals?: boolean,
): { display: string; raw: string } {
  const numValue = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(numValue) || (numValue === 0 && !allowZero)) {
    return { display: "", raw: "" }
  }
  const digits = decimals ?? (allowDecimals ? 2 : 0)
  const display = fixedDecimals
    ? numValue.toLocaleString(getNumberFormatLocale(), {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : formatNumber(numValue, digits)
  return { display, raw: numValue.toString() }
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      onChange,
      onCommit,
      allowDecimals = false,
      allowNegative = false,
      allowZero = false,
      min = allowNegative ? -Infinity : 0,
      max = Infinity,
      clampToRange = false,
      onRefusalChange,
      decimals,
      fixedDecimals,
      locale = getNumberFormatLocale(),
      onBlur,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = React.useState(
      () =>
        computeNumericInputState(value, allowZero, allowDecimals, decimals, fixedDecimals).display,
    )
    // A ref, not state: the blur an Enter triggers runs in the same tick and must see the commit.
    const rawRef = useRef(
      computeNumericInputState(value, allowZero, allowDecimals, decimals, fixedDecimals).raw,
    )

    const internalRef = useRef<HTMLInputElement>(null)
    const cursorPositionRef = useRef<number | null>(null)

    // Under the field: once typing pauses, at once when a commit refuses the value.
    const [shownRefusal, setShownRefusal] = React.useState<string | null>(null)
    const refusalTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
    useEffect(() => () => clearTimeout(refusalTimerRef.current), [])
    const showRefusal = (refusal: string | null, delayMs = 0) => {
      clearTimeout(refusalTimerRef.current)
      if (refusal == null || delayMs === 0) setShownRefusal(refusal)
      else refusalTimerRef.current = setTimeout(() => setShownRefusal(refusal), delayMs)
    }

    useImperativeHandle(ref, () => internalRef.current!)

    const isDeLocale = locale === "de-DE"
    const thousandSep = isDeLocale ? "." : ","
    const thousandSepRegex = isDeLocale ? /\./g : /,/g

    // Helper function to calculate new cursor position after formatting
    const calculateNewCursorPosition = React.useCallback(
      (oldValue: string, newValue: string, oldCursorPos: number): number => {
        // Count thousand separators before cursor in old value
        const beforeCursor = oldValue.slice(0, oldCursorPos)
        const separatorsBefore = (beforeCursor.match(thousandSepRegex) || []).length

        // Calculate character position excluding separators
        const charPosition = oldCursorPos - separatorsBefore

        // Find position in new value by counting non-separator characters
        let newPos = 0
        let charCount = 0

        for (let i = 0; i < newValue.length && charCount < charPosition; i++) {
          if (newValue[i] !== thousandSep) {
            charCount++
          }
          newPos++
        }

        return newPos
      },
      [thousandSep, thousandSepRegex],
    )

    const formatNumberForDisplay = React.useCallback(
      (num: number | string): string =>
        computeNumericInputState(num, allowZero, allowDecimals, decimals, fixedDecimals).display,
      [allowDecimals, allowZero, decimals, fixedDecimals],
    )

    const parseDisplayValue = React.useCallback(
      (displayVal: string): string => {
        // Remove thousand separators
        let cleaned = displayVal.replace(thousandSepRegex, "")

        // Handle decimal separator if decimals are allowed
        if (isDeLocale) {
          if (allowDecimals && cleaned.includes(",")) {
            const parts = cleaned.split(",")
            if (parts.length === 2) {
              cleaned = parts[0] + "." + parts[1]
            }
          }
        }
        // en-US: decimal separator is already ".", no conversion needed

        return cleaned
      },
      [allowDecimals, isDeLocale, thousandSepRegex],
    )

    // Typing not yet committed beats a new value from outside, such as another planner's edit. That
    // value is shown once typing ends: a rejected commit refetches it unchanged, so nothing else would.
    const valueArrivedWhileTypingRef = useRef(false)
    const [resyncAfterTyping, setResyncAfterTyping] = React.useState(0)
    const closeEditRef = useRef<(() => void) | null>(null)
    const closeEdit = () => {
      closeEditRef.current?.()
      closeEditRef.current = null
      if (valueArrivedWhileTypingRef.current) {
        valueArrivedWhileTypingRef.current = false
        setResyncAfterTyping((n) => n + 1)
      }
    }
    useEffect(() => () => closeEditRef.current?.(), [])

    React.useEffect(() => {
      if (closeEditRef.current) {
        valueArrivedWhileTypingRef.current = true
        return
      }
      const numValue = typeof value === "string" ? parseFloat(value) : value
      if (!isNaN(numValue) && (numValue !== 0 || allowZero)) {
        const formatted = formatNumberForDisplay(numValue)
        setDisplayValue(formatted)
        rawRef.current = numValue.toString()
      } else {
        setDisplayValue("")
        rawRef.current = ""
      }
    }, [value, formatNumberForDisplay, allowZero, resyncAfterTyping])

    // Restore cursor position after display value changes
    useLayoutEffect(() => {
      if (cursorPositionRef.current !== null && internalRef.current) {
        internalRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current)
        cursorPositionRef.current = null
      }
    }, [displayValue])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const typed = e.nativeEvent as InputEvent
      const cursorPos = e.target.selectionStart ?? 0
      const inputValue =
        isDeLocale && allowDecimals && typed.inputType === "insertText" && typed.data === "."
          ? typedDotAsDecimal(e.target.value, cursorPos)
          : e.target.value
      const oldDisplayValue = displayValue
      const cleanValue = parseDisplayValue(inputValue)

      // A "-" is kept even where negatives aren't allowed: dropping the keystroke would turn a typed
      // -50 into 50. Commit refuses it instead.
      const validationPattern = allowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/

      if (cleanValue === "" || validationPattern.test(cleanValue)) {
        closeEditRef.current ??= beginEdit()
        rawRef.current = cleanValue
        const refusal = typedRefusal(cleanValue, min, max)
        onRefusalChange?.(refusal)
        showRefusal(refusal, REFUSAL_DELAY_MS)

        if (cleanValue === "") {
          setDisplayValue("")
          cursorPositionRef.current = null
        } else {
          // Format integer part with thousand separators; preserve the user's
          // decimal tail verbatim so a trailing separator (e.g. "79.") or
          // trailing zeros (e.g. "79.50") survive while typing — otherwise
          // parseFloat → toLocaleString silently strips them and the next
          // keystroke lands in the wrong position.
          const dotIdx = cleanValue.indexOf(".")
          const intPart = dotIdx >= 0 ? cleanValue.slice(0, dotIdx) : cleanValue
          const decPart = dotIdx >= 0 ? cleanValue.slice(dotIdx + 1) : null

          const isNegative = intPart.startsWith("-")
          const intDigits = isNegative ? intPart.slice(1) : intPart
          const formattedInt = `${isNegative ? "-" : ""}${groupTypedDigits(intDigits, locale)}`

          const decSep = isDeLocale ? "," : "."
          const newFormattedValue =
            decPart === null ? formattedInt : `${formattedInt}${decSep}${decPart}`

          setDisplayValue(newFormattedValue)
          cursorPositionRef.current = calculateNewCursorPosition(
            oldDisplayValue,
            newFormattedValue,
            cursorPos,
          )
        }
      }
    }

    const restore = () => {
      const restored = computeNumericInputState(
        value,
        allowZero,
        allowDecimals,
        decimals,
        fixedDecimals,
      )
      setDisplayValue(restored.display)
      rawRef.current = restored.raw
      onRefusalChange?.(null)
      closeEdit()
    }

    // One path for Enter and blur. Only a typed edit commits, and committing closes the edit, so a
    // focus and blur, or the blur an Enter triggers, changes nothing.
    const commit = () => {
      if (closeEditRef.current == null) return
      let num = /\d/.test(rawRef.current) ? parseFloat(rawRef.current) : null
      const refusal = typedRefusal(rawRef.current, min, max)
      // Refused, the typed value stays with its message and the edit stays open: Escape restores.
      // Clamped, the bound replaces it, so there is nothing left to explain.
      showRefusal(clampToRange ? null : refusal)
      if (refusal && !clampToRange) return onRefusalChange?.(refusal)
      onRefusalChange?.(null)
      if (refusal) {
        num = Math.min(max, Math.max(min, num!))
        rawRef.current = num.toString()
      }
      if (num != null) {
        onChange?.(num)
        setDisplayValue(num !== 0 || allowZero ? formatNumberForDisplay(num) : "")
      } else if (allowZero) {
        onChange?.(0)
        setDisplayValue(formatNumberForDisplay(0))
      }
      onCommit?.(num)
      // An emptied field shows the value again until the owner decides what emptying means.
      if (num == null) return restore()
      closeEdit()
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      commit()
      onBlur?.(e)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") commit()
      if (e.key === "Escape" && closeEditRef.current) {
        showRefusal(null)
        restore()
      }
      onKeyDown?.(e)
    }

    return (
      <Input
        ref={internalRef}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...props}
        error={shownRefusal ?? props.error}
      />
    )
  },
)
NumericInput.displayName = "NumericInput"

export { DebouncedInput, Input, NumericInput, inputVariants }
